/* Guitar neck (SVG): strings 1..6 top to bottom, optional note marks and tap input. */
import { useEffect, useRef, useState } from 'react';
import { fretMidi, mod, shortPc } from '../core/theory';

export interface FretMark {
  /** string 1..6 */
  s: number;
  f: number;
  /** m-note | m-root | m-hint | m-live | m-tap */
  cls?: string;
  label?: string | null;
}

export interface FretboardProps {
  from?: number;
  to?: number;
  marks?: readonly FretMark[];
  onPick?: (string: number, fret: number, midi: number) => void;
  numbers?: boolean;
  fretW?: number;
  /** Scroll the neck so this fret is centred (for positions far up the neck). */
  scrollToFret?: number;
}

const GAUGE = [1.1, 1.4, 1.8, 2.2, 2.7, 3.2];
const INLAYS = [3, 5, 7, 9, 15, 17, 19, 21];

export function Fretboard({
  from = 0,
  to = 12,
  marks = [],
  onPick,
  numbers = true,
  fretW = 54,
  scrollToFret,
}: FretboardProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const [flashes, setFlashes] = useState<{ id: number; s: number; f: number }[]>([]);
  const flashId = useRef(0);
  const base = from === 0 ? 0 : from - 1;
  const left = from === 0 ? 40 : 14;
  const sh = 21;
  const top = 16;
  const xF = (f: number) => left + (f - base) * fretW;
  const W = xF(to) + 10;
  const H = top * 2 + sh * 5 + (numbers ? 18 : 0);
  const cx = (f: number) => (f === 0 ? left - 18 : (xF(f - 1) + xF(f)) / 2);
  const cy = (sn: number) => top + (sn - 1) * sh;
  const boardTop = top - 9;
  const boardH = sh * 5 + 18;

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll once per target fret
  useEffect(() => {
    if (scrollToFret == null || !wrap.current) return;
    const el = wrap.current;
    const id = requestAnimationFrame(() => {
      el.scrollLeft = Math.max(0, cx(scrollToFret) - el.clientWidth / 2);
    });
    return () => cancelAnimationFrame(id);
  }, [scrollToFret]);

  const pick = (sn: number, f: number) => {
    onPick?.(sn, f, fretMidi(sn, f));
    const id = ++flashId.current;
    setFlashes((x) => [...x, { id, s: sn, f }]);
    setTimeout(() => setFlashes((x) => x.filter((y) => y.id !== id)), 450);
  };

  const frets = [];
  for (let f = Math.max(1, from); f <= to; f++) {
    const x = cx(f);
    if (INLAYS.includes(f)) frets.push(<circle key={`i${f}`} cx={x} cy={top + 2.5 * sh} r={5} className="fb-inlay" />);
    if (f === 12 || f === 24) {
      frets.push(<circle key={`i${f}a`} cx={x} cy={top + 1.5 * sh} r={5} className="fb-inlay" />);
      frets.push(<circle key={`i${f}b`} cx={x} cy={top + 3.5 * sh} r={5} className="fb-inlay" />);
    }
  }
  for (let f = base; f <= to; f++) {
    const isNut = f === 0;
    frets.push(
      <rect
        key={`f${f}`}
        x={xF(f) - (isNut ? 3 : 1.2)}
        y={boardTop}
        width={isNut ? 6 : 2.4}
        height={boardH}
        className={isNut ? 'fb-nut' : 'fb-fret'}
      />,
    );
  }
  const strings = [1, 2, 3, 4, 5, 6].map((sn) => (
    <line
      key={`s${sn}`}
      x1={from === 0 ? 4 : xF(base)}
      x2={xF(to)}
      y1={cy(sn)}
      y2={cy(sn)}
      className="fb-string"
      strokeWidth={GAUGE[sn - 1]}
    />
  ));
  const nums = [];
  if (numbers) {
    for (let f = Math.max(1, from); f <= to; f++)
      nums.push(
        <text key={`n${f}`} x={cx(f)} y={H - 3} className={`fb-num${[3, 5, 7, 9, 12].includes(f) ? ' strong' : ''}`}>
          {f}
        </text>,
      );
    if (from === 0)
      nums.push(
        <text key="n0" x={cx(0)} y={H - 3} className="fb-num">
          0
        </text>,
      );
  }
  const hits = [];
  if (onPick) {
    for (let sn = 1; sn <= 6; sn++) {
      for (let f = from; f <= to; f++) {
        const x0 = f === 0 ? 0 : xF(f - 1);
        const x1 = f === 0 ? left - 2 : xF(f);
        hits.push(
          <rect
            key={`h${sn}.${f}`}
            x={x0}
            y={cy(sn) - sh / 2}
            width={x1 - x0}
            height={sh}
            className="fb-hit"
            tabIndex={-1}
            data-s={sn}
            data-f={f}
            onPointerDown={(e) => {
              e.preventDefault();
              pick(sn, f);
            }}
          />,
        );
      }
    }
  }
  return (
    <div className="fb-wrap" ref={wrap}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        className={`fretboard${onPick ? ' pickable' : ''}`}
        role="img"
        aria-label="Gitar sapı"
      >
        <rect x={xF(base)} y={boardTop} width={xF(to) - xF(base)} height={boardH} rx={3} className="fb-wood" />
        {frets}
        {strings}
        {nums}
        <g className="fb-marks">
          {marks
            .filter((m) => m.f >= from && m.f <= to)
            .map((m) => {
              const label = m.label == null ? '' : String(m.label);
              const r = label.length > 3 ? 11 : 9.5;
              return (
                <g key={`m${m.s}.${m.f}.${m.cls ?? ''}`} className={`fb-mark ${m.cls || ''}`}>
                  <circle cx={cx(m.f)} cy={cy(m.s)} r={r} />
                  {label ? (
                    <text x={cx(m.f)} y={cy(m.s) + 3.4} className={label.length > 3 ? 'small' : ''}>
                      {label}
                    </text>
                  ) : null}
                </g>
              );
            })}
          {flashes.map((fl) => (
            <g key={`fl${fl.id}`} className="fb-mark fb-flash m-tap">
              <circle cx={cx(fl.f)} cy={cy(fl.s)} r={9.5} />
            </g>
          ))}
        </g>
        <g className="fb-hits">{hits}</g>
      </svg>
    </div>
  );
}

/** Marks every position of the given pitch classes; the root gets the accent colour. */
export function pcMarks(
  pcs: readonly number[],
  o: { from?: number; to?: number; root?: number | null; labels?: boolean } = {},
): FretMark[] {
  const set = new Set(pcs.map((p) => mod(p, 12)));
  const out: FretMark[] = [];
  for (let sn = 1; sn <= 6; sn++)
    for (let f = o.from || 0; f <= (o.to == null ? 12 : o.to); f++) {
      const pc = mod(fretMidi(sn, f), 12);
      if (set.has(pc))
        out.push({ s: sn, f, cls: pc === o.root ? 'm-root' : 'm-note', label: o.labels === false ? '' : shortPc(pc) });
    }
  return out;
}
